// !------------- Core React and State Management -------------!
// !--- react ---!
import React, { useState, useEffect } from 'react';
// Documentation: https://reactjs.org/

// !------------- Feedback Storage, ID -------------!
// !--- airtable ---!
import Airtable from 'airtable';
// Documentation: https://github.com/airtable/airtable.js

// !--- uuid ---!
import { v4 as uuidv4 } from 'uuid';
// Documentation: https://github.com/uuidjs/uuid#readme

// !------------- Modal Features -------------!
// !--- react-modal ---!
import Modal from 'react-modal';
// Documentation: https://reactcommunity.org/react-modal/

// !------------- Custom Components and Styles -------------!
import './FeedbackForm.css';

/**
 * Functional component to render an input or textarea field based on its type.
 * 
 * @param {object} field - Object containing field configurations like name, label, and required.
 * @param {string} value - The current value of the input field.
 * @param {function} handleChange - Function to handle changes in the input field.
 * @returns {JSX.Element} - Returns a JSX element of either input or textarea based on the field type.
 */
const InputField = ({ field, value, handleChange }) => {
    // Conditional rendering based on field type
    if (field.type === 'textarea') {
        // Textarea field rendering
        return (
            <label>
                {field.label}:
                <textarea 
                    name={field.name} 
                    value={value} 
                    onChange={handleChange} 
                    required={field.required}
                    style={{ minHeight: '50px', overflow: 'hidden' }}
                    onInput={e => {
                        e.target.style.height = 'auto';
                        e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                />
            </label>
        );
    } else {
        // Default input field rendering
        return (
            <label>
                {field.label}:
                <input 
                    type="text" 
                    name={field.name} 
                    value={value} 
                    onChange={handleChange} 
                    required={field.required} 
                />
            </label>
        );
    }
};

/**
 * Functional component to render a select dropdown.
 * 
 * @param {object} field - Object containing field configurations like name, label, and required.
 * @param {string} value - The current value of the select field.
 * @param {function} handleChange - Function to handle changes in the select field.
 * @returns {JSX.Element} - Returns a JSX element of a select dropdown.
 */
const SelectField = ({ field, value, handleChange }) => (
    <label>
        {field.label}:
        <select 
            name={field.name} 
            value={value} 
            onChange={handleChange} 
            required={field.required}
        >
            {field.options.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
    </label>
);

/**
 * Main component for the feedback form which includes input and select fields.
 * 
 * @param {boolean} isOpen - State indicating if the modal should be open.
 * @param {function} closeModal - Function to close the modal.
 * @param {string} dashboardName - The name of the dashboard to be included in the feedback.
 * @param {object} colors - Color settings for the form.
 * @returns {JSX.Element} - Returns a JSX element of the feedback form modal.
 */
export default function FeedbackForm({ isOpen, closeModal, dashboardName, colors }) {
    // State for form values initialization
    const initialFormState = {
        name: '',
        category: 'New Feature Request',
        feedback: ''
    };

    // State hooks for managing form state
    const [formState, setFormState] = useState(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Key for Airtable API, falls back to a default key if not set
    const airtableKey = process.env.REACT_APP_AIRTABLE || 'defaultApiKey';
    if (!process.env.REACT_APP_AIRTABLE) {
        console.warn('REACT_APP_AIRTABLE environment variable is not set.');
    }
    // Airtable base configuration
    const storage = new Airtable({ apiKey: airtableKey }).base('appMWugz0s9Whv6yC');

    // Effect to adjust CSS variables and focus based on modal state
    useEffect(() => {
        document.documentElement.style.setProperty('--main-color', colors.main);
        if (isOpen) {
            const nameInput = document.querySelector('input[name="name"]');
            if (nameInput) nameInput.focus();
        }
    }, [isOpen, colors.main]);

    /**
     * Handles the submission of the feedback form.
     * 
     * @param {Event} e - Event object to prevent default form submission.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        const feedbackId = uuidv4();  // Generate unique ID for feedback

        // Create a new record in Airtable
        storage('tblwCdmz2FPHmWX7k').create([{
            fields: {
                "Name": formState.name,
                "Category": formState.category,
                "Feedback": formState.feedback,
                "Dashboard": dashboardName,
                "Feedback ID": feedbackId
            }
        }], (err, records) => {
            setIsSubmitting(false);
            if (err) {
                console.error(err);
                setError('Failed to submit feedback.');
                return;
            }
            console.log('Feedback submitted to Airtable:', records);
            setFormState(initialFormState);
            closeModal();
        });
    };

    /**
     * Handles changes in form inputs and updates the state accordingly.
     * 
     * @param {Event} e - Event object that carries the current field value.
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // JSX for rendering the modal with form
    return (
        <Modal isOpen={isOpen} onRequestClose={closeModal} contentLabel="Feedback Form" className="response-modal">
            <form onSubmit={handleSubmit} className="response-form">
                <InputField field={{ name: 'name', type: 'text', label: 'Name', required: true }} value={formState.name} handleChange={handleChange} />
                <SelectField field={{ name: 'category', type: 'select', label: 'Category', options: ['New Feature Request', 'Fix/Bug', 'Remove'], required: true }} value={formState.category} handleChange={handleChange} />
                <InputField field={{ name: 'feedback', type: 'textarea', label: 'Feedback', required: true }} value={formState.feedback} handleChange={handleChange} />
                {error && <div className="error-message">{error}</div>}
                <button type="submit" className="response-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
            </form>
        </Modal>
    );
}
